import { useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from "@mui/material";
import { Refresh, Search, NotificationsActive } from "@mui/icons-material";
import PropTypes from "prop-types";
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import AlertsTable from "./components/AlertsTable";
import AlertsPanel from "./components/AlertsPanel";
import { useAlertas } from "./hooks/useAlertas";
import { formatAlertDate } from "./model/alertsModel";

export function AlertsContent({ controller }) {
  const [notification, setNotification] = useState(null);
  const {
    alerts,
    allAlerts,
    stats,
    loading,
    error,
    activeFilter,
    typeFilter,
    searchTerm,
    socketConnected,
    reconnecting,
    lastUpdated,
    refreshData,
    setFilter,
    setTypeFilter,
    setSearch,
  } = controller;
  const refresh = async () => {
    const result = await refreshData();
    setNotification(
      result?.success
        ? { severity: "success", message: "Alertas actualizadas" }
        : { severity: "error", message: result?.error || "No se pudieron actualizar las alertas" }
    );
  };
  const filtered = activeFilter !== "all" || typeFilter !== "all" || Boolean(searchTerm.trim());
  return (
    <Box sx={{ py: 3 }}>
      <AlertsPanel controller={controller} />
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          bgcolor: "#193e54",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          color: "#fff",
        }}
      >
        <NotificationsActive />
        <Box sx={{ flex: "1 1 260px" }}>
          <Typography component="h1" sx={{ color: "#fff", fontSize: "1.15rem", fontWeight: 700 }}>
            Alertas de equipos
          </Typography>
          <Typography variant="body2" sx={{ color: "#dbe8f2", mt: 0.5 }}>
            {stats.total} alertas reportadas por el sistema
          </Typography>
        </Box>
        <Chip
          size="small"
          label={
            socketConnected
              ? "Tiempo real conectado"
              : reconnecting
              ? "Reconectando"
              : "Tiempo real desconectado"
          }
          sx={{ bgcolor: "#ffffff14", color: "#fff", border: "1px solid #ffffff40" }}
        />
        <Button startIcon={<Refresh />} onClick={refresh} disabled={loading} sx={{ color: "#fff" }}>
          {loading ? "Actualizando…" : "Actualizar"}
        </Button>
      </Paper>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          ["all", "Total", stats.total, "#eaf2ff", "#245a83"],
          ["critical", "Críticas", stats.critical, "#fff1f0", "#b42318"],
          ["warning", "Advertencias", stats.warning, "#fff6e6", "#925600"],
          ["info", "Información", stats.info, "#edf5f8", "#245a83"],
        ].map(([key, label, value, background, color]) => (
          <Grid item xs={6} md={3} key={key}>
            <ButtonBase
              aria-label={`Filtrar ${label}`}
              aria-pressed={activeFilter === key}
              onClick={() => setFilter(key)}
              sx={{
                p: 2,
                width: "100%",
                borderRadius: 2,
                border: `1px solid ${activeFilter === key ? color : "#dce5ed"}`,
                bgcolor: background,
                justifyContent: "space-between",
                color,
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", color }}>{label}</Typography>
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color }}>{value}</Typography>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          label="Buscar mensaje, comercio, equipo, IP o ID"
          value={searchTerm}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Tipo"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 150, "& .MuiInputBase-root": { minHeight: 38 } }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {[...new Set(allAlerts.map((item) => item.alertType))].sort().map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <Button
          disabled={!filtered}
          onClick={() => {
            setFilter("all");
            setTypeFilter("all");
            setSearch("");
          }}
        >
          Limpiar filtros
        </Button>
      </Paper>
      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "#526579" }}>
        {alerts.length} de {stats.total} alertas · Hora de Bogotá
      </Typography>
      <AlertsTable
        hasFilters={filtered}
        alerts={alerts}
        loading={loading}
        error={error}
        onRefresh={refresh}
        resetKey={`${activeFilter}|${typeFilter}|${searchTerm}`}
      />
      <Typography variant="caption" sx={{ mt: 2, display: "block", color: "#526579" }}>
        Última actualización correcta:{" "}
        {lastUpdated ? formatAlertDate(lastUpdated) : "Sin actualización"}
      </Typography>
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={5000}
        onClose={() => setNotification(null)}
      >
        <Alert severity={notification?.severity || "info"} onClose={() => setNotification(null)}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
AlertsContent.propTypes = { controller: PropTypes.object.isRequired };
export default function Alertas() {
  const controller = useAlertas();
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <AlertsContent controller={controller} />
    </DashboardLayout>
  );
}
